import { createSelector } from "reselect"
import { get, map, find, chain, filter } from "lodash"
import { getFactorCategory, getFactorLabel } from "helpers/factors"
import { getCurrentPerformance } from "./performance"
import pluralize from "pluralize"
import { getFavoriteFactors } from "modules/portfolio-root/selectors/portfolios"
import { getModelFactors, getModelCategories } from "selectors/modelMetadata"

const OVERALL_ITEMS = [{
  id: "total",
  color: "#00abe8",
  label: "Total",
  field: "total",
  type: "overall",
}, {
  id: "factor",
  color: "#9067a7",
  label: "Factor",
  field: "factorsTotal",
  type: "overall",
}, {
  id: "alpha",
  color: "#627b8c",
  label: "Alpha",
  field: "alpha",
  type: "overall",
}, {
  id: "tradingPnl",
  color: "#da7c30",
  label: "Trading",
  field: "trading",
  type: "overall",
}]

const getRawDisplayType = state => get(state, "modules.securityProfilePerformance.displayType")
const getDisabled = state => get(state, "modules.securityProfilePerformance.disabled")

export const getDisplayType = createSelector(
  [getRawDisplayType, getModelCategories],
  (displayType, categories) => {
    if (displayType === "total" || displayType === "factors") {
      return displayType
    } else if (find(categories, cat => cat.id === displayType)) {
      return displayType
    }

    return "total"
  },
)

const getComputedFactors = createSelector(
  [getModelFactors, getCurrentPerformance, getDisabled, getFavoriteFactors],
  (factorsMetadata, lastDatum, disabled, favoriteFactors) => {
    let factors = chain(lastDatum)
      .get("factors")
      .map((risk, id) => ({ id, lastValue: risk }))
      .value()

    factors = factors.map(factor => ({
      id: factor.id,
      label: getFactorLabel(factorsMetadata, factor.id),
      category: getFactorCategory(factorsMetadata, factor.id),
      favorite: favoriteFactors[factor.id],
      lastValue: factor.lastValue,
      field: `factors.${factor.id}`,
      disabled: !!disabled[factor.id],
      type: "factors",
    }))

    return factors
  },
)

const getOverallItems = createSelector(
  [getModelCategories, getDisabled],
  (categories, disabled) => {
    return [
      ...map(OVERALL_ITEMS, item => ({ ...item, disabled: !!disabled[item.id] })),
      {
        id: "factor_total",
        color: "#9067a7",
        label: "All Factors",
        categoryLabel: "Factors",
        field: "factorsTotal",
        disabled: !!disabled.factor_total,
        type: "category",
      },
      ...map(categories, category => ({
        id: category.id,
        color: category.color,
        label: category.label,
        field: `categories.${category.id}`,
        disabled: !!disabled[category.id],
        type: "category",
      })),
      ...map(categories, category => ({
        id: `${category.id}_total`,
        color: category.color,
        categoryLabel: pluralize(category.label),
        category: category.id,
        label: `All ${category.label} Factors`,
        field: `categories.${category.id}`,
        disabled: !!disabled[`${category.id}_total`],
      })),
    ]
  },
)

export const getAllItems = createSelector(
  [getComputedFactors, getOverallItems],
  (factors, overallItems) => ([
    ...overallItems,
    ...factors,
  ]),
)

const getAllCategoryItems = createSelector(
  [getComputedFactors, getOverallItems, getDisplayType],
  (factors, overallItems, displayType) => {
    switch (displayType) {
      case "total":
        return filter(overallItems, { type: "overall" })
      case "factors":
        return chain(overallItems)
          .filter({ type: "category" })
          .value()
      default: {
        const overall = chain(overallItems)
          .filter({ category: displayType })
          .value()

        return [
          ...overall,
          ...chain(factors)
            .filter({ category: displayType })
            .orderBy([item => Math.abs(item.lastValue)], ["desc"])
            .value(),
        ]
      }
    }
  },
)

export const getOrderedCategoryItems = createSelector(
  [getAllCategoryItems, getDisplayType],
  (items, displayType) => {
    if (displayType !== "total" && displayType !== "factors") {
      const overall = items[0]
      const factors = items.slice(1)
      return [
        overall,
        ...chain(factors)
          .orderBy([item => item.favorite, item => Math.abs(item.lastValue)], ["asc", "desc"])
          .value(),
      ]
    }

    return items
  },
)
